import { SavedOutput } from '@/api/types';
import { getLocalItem, setLocalItem, getOutputsKey } from './storage';
import { APP_ERRORS } from '@/constants/errors';

export const getSavedOutputs = async (workspaceId: string): Promise<{ items: SavedOutput[] }> => {
  const outputs = await getLocalItem<SavedOutput[]>(getOutputsKey(workspaceId), []);
  return { items: outputs };
};

export const getSavedOutput = async (workspaceId: string, id: string): Promise<SavedOutput> => {
  const outputs = await getLocalItem<SavedOutput[]>(getOutputsKey(workspaceId), []);
  const output = outputs.find(o => o.id === id);
  if (!output) throw new Error('Saved output not found');
  return output;
};

export const saveOutput = async (workspaceId: string, payload: Omit<SavedOutput, 'id' | 'created'>): Promise<{ output: SavedOutput, pruned: false }> => {
  let outputs = await getLocalItem<SavedOutput[]>(getOutputsKey(workspaceId), []);
  
  // Limits outputs to 20
  if (outputs.length >= 20) {
    throw new Error(APP_ERRORS.OUTPUT_STORE_FULL.message);
  }

  const newOutput: SavedOutput = {
    ...payload,
    id: Math.random().toString(36).substring(7),
    created: new Date().toISOString(),
    status: payload.status || 'To Apply', // default status
  };
  outputs.unshift(newOutput); // Prepend to list
  await setLocalItem(getOutputsKey(workspaceId), outputs);
  return { output: newOutput, pruned: false };
};

export const updateSavedOutput = async (workspaceId: string, id: string, updates: Partial<SavedOutput>): Promise<SavedOutput> => {
  let outputs = await getLocalItem<SavedOutput[]>(getOutputsKey(workspaceId), []);
  const index = outputs.findIndex(o => o.id === id);
  if (index === -1) throw new Error('Saved output not found');
  
  const updatedOutput = { ...outputs[index], ...updates, created: new Date().toISOString() };
  outputs.splice(index, 1);
  outputs.unshift(updatedOutput);
  await setLocalItem(getOutputsKey(workspaceId), outputs);
  return updatedOutput;
};

export const deleteSavedOutput = async (workspaceId: string, id: string): Promise<void> => {
  let outputs = await getLocalItem<SavedOutput[]>(getOutputsKey(workspaceId), []);
  outputs = outputs.filter(o => o.id !== id);
  await setLocalItem(getOutputsKey(workspaceId), outputs);
};

